import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

import { ref, onMounted, watchEffect, toRaw } from 'vue';
import { useRoute } from 'vue-router';
import { configRegistry } from '@/lib/configRegistry';
import { remoteOptionsRegistry } from '@/lib/remoteOptions';
import { useFloatFormStore } from '@/store/floatForm';
import type { FormField, FormProps } from '@/types/form';


export default /*@__PURE__*/_defineComponent({
  __name: 'CreateForm',
  setup(__props) {

const route = useRoute();
const moduleName = (route.meta.moduleName ?? route.name) as string;
const floatFormStore = useFloatFormStore();

const props = floatFormStore.props as FormProps;
const onCreate = props?.onCreate;
const formTitle = floatFormStore.formTitle;

const formFields = ref<FormField[]>([]);
const form = ref<Record<string, any>>({});
const formValid = ref(true);
const nestedValid = ref(true);
const formRef = ref();
const visibleFields = ref<FormField[]>([]);

watchEffect(() => {
  visibleFields.value = formFields.value
    .map((field) => {
      const val = form.value[field.dependsOn as any];
      const condition = field.dependsCondition;
      let conditionMet = true;

      if (field.dependsOn && field.dependsBehavior) {
        switch (condition?.condition) {
          case 'diferent':
            conditionMet = val !== undefined && val !== '' && val !== condition.value;
            break;
          case 'equals':
            conditionMet = val === condition.value;
            break;
          case 'includes':
            conditionMet = !val?.toLowerCase?.().includes(condition.value);
            break;
        }

        if (field.dependsBehavior === 'hidden' && !conditionMet) return null;
        if (field.dependsBehavior === 'disabled' && !conditionMet) {
          return { ...field, permission: 'disabled' };
        }
      }

      if (field.permission === 'hidden') return null;

      return field;
    })
    .filter((f): f is FormField => !!f);
});

const getFieldProps = (field: FormField) => {
  const props: Record<string, any> = {
    label: field.label,
    placeholder: field.placeholder ?? '',
    clearable: true,
    density: 'compact',
    type: field.type || 'text',
    prefix: field.prefix,
    suffix: field.suffix,
    'prepend-inner-icon': field.icon,
    disabled: field.permission === 'disabled',
    readonly: field.readonly,
    multiple: field.multiple || false,
    'item-title': field.itemText || 'label',
    'item-value': field.itemValue || 'value',
    'hide-no-data': true,
    range:field.range
  };

  if (field.options) props.items = field.options;

  if (field.fetchOptions && remoteOptionsRegistry[field.fetchOptions]) {
    props['onUpdate:search'] = async (val: string) => {
      const fetchFn = remoteOptionsRegistry[field.fetchOptions as any];
      let results = [];

      if (field.fetchParams) {
        const params: Record<string, any> = field.fetchParams(form.value) || {};

        if (typeof field.itemText === 'string' && val && val.length >= 1) {
          params[`filter[${field.itemText}]`] = val;
        }

        results = await fetchFn(params as any);
      } else {
        if (val && val.length >= 1) {
          results = await fetchFn(val);
        }
      }

      props['hide-no-data'] = !(val && val.length >= 1);

      field.options = results;
      props.items = results;
    };
  }

  return props;
};

const onNestedValidation = (valid: boolean) => {
  nestedValid.value = valid;
};

const resetForm = () => {
  formFields.value.forEach((f) => {
    if (f.isJsonArray && f.plainJson && f.children) {
      f.children.forEach((c) => (form.value[c.value] = ''));
    } else {
      form.value[f.value] = f.multiple ? [] : '';
    }
  });
};

const closeForm = () => {
  resetForm();
  floatFormStore.closeForm();
};

const flattenForm = (form: Record<string, any>, fields: FormField[]) => {
  const flatForm: Record<string, any> = {};
  for (const field of fields) {
    const val = form[field.value];

    if (typeof field.transform === 'function') {
      const result = field.transform(toRaw(val), form);
      if (typeof result === 'object' && !Array.isArray(result)) {
        Object.assign(flatForm, result);
      } else {
        flatForm[field.value] = result;
      }
      continue;
    }

    if (field.isJsonArray && field.children) {
      if(field.plainJson){
        for (const child of field.children) {
          flatForm[child.value] = val?.[child.value] ?? null;
        }
      }else{
        flatForm[field.value] = toRaw(val);
      }
    } else if (field.multiple && Array.isArray(val)) {
      flatForm[field.value] = val.map((v: any) =>
        typeof v === 'object' ? v[field.itemValue || 'value'] ?? v.id ?? null : v
      );
    } else if (typeof val === 'object' && val !== null) {
      flatForm[field.value] = val[field.itemValue || 'value'] ?? val.id ?? null;
    } else {
      flatForm[field.value] = val ?? null;
    }
  }

  return flatForm;
};

const submitForm = async () => {
  if (!formRef.value?.validate() || !nestedValid.value) return;

  try {
    const payload = flattenForm(form.value, formFields.value);
    if (onCreate) await onCreate(payload);
    else console.warn('[CreateForm] No se proporcionó función onCreate');
    closeForm();
  } catch (err) {
    console.error('[CreateForm] Error en onCreate', err);
  }
};

onMounted(async () => {
  const loader = configRegistry[moduleName];
  if (!loader) {
    console.error(`[CreateForm] Config para ${moduleName} no encontrada.`);
    return;
  }

  try {
    const mod = await loader();
    formFields.value = mod.CREATE_FORM || [];
    
    const initialValues = props.initialValues ?? {};

    for (const f of formFields.value) {
      const initial = initialValues[f.value];
      form.value[f.value] = initial ?? (f.multiple ? [] : '');
    }

  } catch (err) {
    console.error('[CreateForm] Error al cargar config:', err);
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, {
    onSubmit: _withModifiers(submitForm, ["prevent"]),
    ref_key: "formRef",
    ref: formRef,
    modelValue: formValid.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formValid).value = $event))
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, { class: "px-5 py-3" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center mb-3" }, {
            default: _withCtx(() => [
              _createElementVNode("span", null, _toDisplayString(_unref(formTitle)), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_text, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_row, null, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleFields.value, (field) => {
                    return (_openBlock(), _createElementBlock(_Fragment, {
                      key: field?.value
                    }, [
                      field
                        ? (_openBlock(), _createBlock(_component_v_col, {
                            key: 0,
                            cols: "12",
                            md: field.col || 12,
                            sm: field.mdcol || 12
                          }, {
                            default: _withCtx(() => [
                              (!field.isJsonArray)
                                ? (_openBlock(), _createBlock(_resolveDynamicComponent(field.component), _mergeProps({
                                    key: 0,
                                    modelValue: form.value[field.value],
                                    "onUpdate:modelValue": ($event: any) => ((form.value[field.value]) = $event),
                                    ref_for: true
                                  }, getFieldProps(field), {
                                    rules: field.required ? [v => !!v || 'Campo obligatorio'] : []
                                  }), null, 16, ["modelValue", "onUpdate:modelValue", "rules"]))
                                : (_openBlock(), _createBlock(_resolveDynamicComponent(field.component), {
                                    key: 1,
                                    modelValue: form.value[field.value],
                                    "onUpdate:modelValue": ($event: any) => ((form.value[field.value]) = $event),
                                    fields: field.children,
                                    summary: field.summary,
                                    label: field.label,
                                    form: form.value,
                                    onValidation: onNestedValidation
                                  }, null, 40, ["modelValue", "onUpdate:modelValue", "fields", "summary", "label", "form"]))
                            ]),
                            _: 2
                          }, 1032, ["md", "sm"]))
                        : _createCommentVNode("", true)
                    ], 64))
                  }), 128))
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_actions, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_btn, {
                variant: "text",
                onClick: closeForm
              }, {
                default: _withCtx(() => _cache[1] || (_cache[1] = [
                  _createTextVNode("Cancelar")
                ])),
                _: 1
              }),
              _createVNode(_component_v_btn, {
                type: "submit",
                disabled: !formValid.value || !nestedValid.value,
                color: "secondary"
              }, {
                default: _withCtx(() => _cache[2] || (_cache[2] = [
                  _createTextVNode("Guardar")
                ])),
                _: 1
              }, 8, ["disabled"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})